import { useEffect, useMemo, useState } from 'react'
import { useAppStore } from '@/store/appStore'
import { getMediaBlob } from '@/db/viewerDb'
import { processMediaUrls } from '@/hooks/useProcessMediaUrls'
import { extractMediaNames, replaceMediaUrls } from '@/utils/mediaReferences'
import type { BundleMediaAsset } from '@/types/api'

/**
 * Module-level reference-counted blob: URL registry.
 * Shared across all useOfflineRichContentMediaUrls instances so that multiple
 * RichContent components referencing the same media share a single blob: URL
 * and it is only revoked when the last consumer unmounts.
 */
const blobRegistry = new Map<string, { objectUrl: string; refCount: number }>()
const objectUrlToKey = new Map<string, string>()

/** Returns the original cache key (system file name or source URL) for a blob: URL, or undefined if not in the registry. */
export function resolveOriginalKey(objectUrl: string): string | undefined {
  return objectUrlToKey.get(objectUrl)
}

function acquireBlobUrl(key: string, blob: Blob): string {
  const existing = blobRegistry.get(key)
  if (existing) {
    existing.refCount++
    return existing.objectUrl
  }
  const objectUrl = URL.createObjectURL(blob)
  blobRegistry.set(key, { objectUrl, refCount: 1 })
  objectUrlToKey.set(objectUrl, key)
  return objectUrl
}

function releaseBlobUrl(key: string): void {
  const entry = blobRegistry.get(key)
  if (!entry) return
  entry.refCount--
  if (entry.refCount <= 0) {
    URL.revokeObjectURL(entry.objectUrl)
    objectUrlToKey.delete(entry.objectUrl)
    blobRegistry.delete(key)
  }
}

const EXTERNAL_URL_ATTR = /(?:src|href)=["'](https?:\/\/[^"']+)["']/gi
const ANCHOR_TAG = /<a\b[^>]*>/gi
const HREF_ATTR = /\bhref=(["'])([^"']*)\1/i
const DOWNLOAD_ATTR = /\sdownload\b/i

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const escapeAttr = (s: string) => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')

function isDownloadOnly(asset: BundleMediaAsset): boolean {
  return !asset.mimeType.startsWith('image/') && asset.mimeType !== 'application/pdf'
}

function addDownloadNames(html: string, fileNameByBlobUrl: Map<string, string>): string {
  if (fileNameByBlobUrl.size === 0) return html
  return html.replace(ANCHOR_TAG, (tag) => {
    const href = HREF_ATTR.exec(tag)?.[2]
    const fileName = href ? fileNameByBlobUrl.get(href) : undefined
    if (!fileName || DOWNLOAD_ATTR.test(tag)) return tag
    return tag.replace(/^<a\b/i, `<a download="${escapeAttr(fileName)}"`)
  })
}

/**
 * Resolves media URLs in rich HTML. Alt text is already embedded server-side.
 *
 * Online: relative /api/media/ references become absolute API URLs.
 * Offline: cached media (and external images) are substituted with blob: URLs from the
 * IndexedDB media cache. Non-image, non-PDF documents also get a `download` attribute set to
 * their original file name, taken from the bundle's media list.
 */
export function useOfflineRichContentMediaUrls(
  html: string | undefined | null,
  media?: BundleMediaAsset[],
): string {
  const { isOffline } = useAppStore()
  const onlineHtml = useMemo(() => processMediaUrls(html), [html])
  const [offline, setOffline] = useState<{ source: string; value: string } | null>(null)

  useEffect(() => {
    if (!isOffline || !html) return

    let cancelled = false
    const keysAcquired = new Set<string>()

    const run = async () => {
      const names = extractMediaNames(html)
      const externalUrls = [...new Set([...html.matchAll(EXTERNAL_URL_ATTR)].map((m) => m[1]))]
      const lookups = await Promise.all(
        [...names, ...externalUrls].map(async (key) => ({ key, entry: await getMediaBlob(key) })),
      )
      if (cancelled) return

      const blobUrlByKey = new Map<string, string>()
      for (const { key, entry } of lookups) {
        if (!entry) continue
        blobUrlByKey.set(key, acquireBlobUrl(key, entry.blob))
        keysAcquired.add(key)
      }

      let processed = replaceMediaUrls(html, (name) => blobUrlByKey.get(name) ?? null)
      for (const url of externalUrls) {
        const blobUrl = blobUrlByKey.get(url)
        if (!blobUrl) continue
        processed = processed.replace(new RegExp(`((?:src|href)=["'])${escapeRegExp(url)}(["'])`, 'gi'), `$1${blobUrl}$2`)
      }

      const fileNameByBlobUrl = new Map<string, string>()
      for (const asset of media ?? []) {
        const blobUrl = blobUrlByKey.get(asset.systemFileName)
        if (blobUrl && isDownloadOnly(asset)) fileNameByBlobUrl.set(blobUrl, asset.originalFileName)
      }
      processed = processMediaUrls(addDownloadNames(processed, fileNameByBlobUrl))

      setOffline({ source: html, value: processed })
    }

    void run()

    return () => {
      cancelled = true
      for (const key of keysAcquired) releaseBlobUrl(key)
    }
  }, [html, isOffline, media])

  if (!isOffline) return onlineHtml
  if (!html) return ''
  return offline?.source === html ? offline.value : html
}
